import React, { useState, useMemo, useEffect } from 'react';

import { calculateRowTotals } from '../../utils/calculations';
import { getBgClass, getIndentClass } from '../../utils/styleUtils';

import ContractRow from './ContractRow';
import ExpenseMilestoneData from './ExpenseMilestoneData';

import { formatFigure, formatToFixedMoneyWithLocale } from 'Helpers/formatService';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronUpIcon from 'Assets/icons/chevron-up.svg';

export default function CommittedSection(props) {
    const [showCommittedChildren, setShowCommittedChildren] = useState(false);

    // Handle expand all trigger from parent with staggered updates
    useEffect(() => {
        const t = props.expandAllTrigger;
        if (!t?.version) return;

        if (t.batch === 'committed' || t.batch === 'projects committed') {
            setShowCommittedChildren(t.expanded);
        }
    }, [props.expandAllTrigger?.version]);

    // Calculate indentation levels
    const indentLevel = props.indentLevel;
    const childIndentLevel = indentLevel + 1;

    const toggleCommittedShow = () => {
        setShowCommittedChildren(!showCommittedChildren);
    };

    const committed = props.expense.committed;
    if (!committed) return null;

    // Memoize contracts filtering to avoid expensive operations on every render
    const contracts = useMemo(() => {
        if (Array.isArray(committed)) {
            return committed.filter((contract) => contract.milestones && contract.milestones.length > 0);
        } else if (typeof committed === 'object') {
            return Object.values(committed).filter((contract) => contract.milestones && contract.milestones.length > 0);
        }
        return [];
    }, [committed]);

    if (contracts.length === 0) return null;

    // Memoize total committed value calculation
    const totalCommittedValue = useMemo(() => {
        return contracts.reduce((sum, contract) => sum + (contract.value || 0), 0);
    }, [contracts]);

    // Memoize aggregated current milestones calculation (most expensive operation)
    const aggregatedCurrentMilestones = useMemo(() => {
        const result = [];
        const { maxExpenseMilestonesCount } = props;

        for (let i = 0; i < maxExpenseMilestonesCount; i++) {
            let totalAmount = 0;
            let milestoneDate = null;

            contracts.forEach((contract) => {
                if (contract.currentMilestones && contract.currentMilestones[i]) {
                    totalAmount += contract.currentMilestones[i].amount || 0;
                    if (!milestoneDate) {
                        milestoneDate = contract.currentMilestones[i].date;
                    }
                }
            });

            result.push({
                amount: totalAmount,
                date: milestoneDate || '',
            });
        }

        return result;
    }, [contracts, props.maxExpenseMilestonesCount]);

    // Memoize common rendering props
    const milestoneElements = useMemo(() => {
        return [...Array(props.maxExpenseMilestonesCount)].map((e, i) => (
            <React.Fragment key={`committed_milestones_${i}`}>
                <ExpenseMilestoneData
                    key={`${props.keyPrefix || ''}committed_${props.expense.id}_milestone_${i}`}
                    milestones={aggregatedCurrentMilestones}
                    milestoneId={i}
                    keyPrefix={props.keyPrefix || ''}
                    amountOnly={true}
                />
            </React.Fragment>
        ));
    }, [props.maxExpenseMilestonesCount, aggregatedCurrentMilestones, props.keyPrefix, props.expense.id]);

    // Memoize contract rows
    const contractRows = useMemo(() => {
        if (Array.isArray(committed)) {
            return committed
                .filter((contract) => contract.milestones && contract.milestones.length > 0)
                .map((contract, index) => (
                    <ContractRow
                        {...props}
                        key={`contract_${index}_${props.expense.id}`}
                        contract={contract}
                        periods={props.periods}
                        expense={props.expense}
                        indentLevel={childIndentLevel}
                    />
                ));
        } else {
            return Object.entries(committed)
                .filter(([contractRef, contract]) => contract.milestones && contract.milestones.length > 0)
                .map(([contractRef, contract]) => (
                    <ContractRow
                        {...props}
                        key={`contract_${contractRef}_${props.expense.id}`}
                        contract={contract}
                        periods={props.periods}
                        expense={props.expense}
                        indentLevel={childIndentLevel}
                    />
                ));
        }
    }, [committed, props, childIndentLevel]);

    const totalValue = useMemo(() => {
        let totalAmount = 0;
        Object.entries(committed).map(([contractRef, contract]) => {
            totalAmount += calculateRowTotals(contract.currentMilestones).totalValue;
        });

        return totalAmount;
    }, [committed]);

    return (
        <>
            <tr className={getBgClass(indentLevel, 'expense')}>
                <td>
                    <div className={`tw-flex tw-items-center tw-gap-x-1.5 tw-cursor-pointer ${getIndentClass(indentLevel)}`} onClick={toggleCommittedShow}>
                        <div className="tw-font-medium tw-text-gray-700">{$t('Committed')}</div>
                        <div>{showCommittedChildren ? <ChevronUpIcon /> : <ChevronDownIcon />}</div>
                    </div>
                </td>
                <td className="tw-p-4"></td>
                <td className="tw-p-4 tw-text-right tw-text-gray-700 tw-border-r-2">
                    {formatFigure(totalValue, formatToFixedMoneyWithLocale, { precision: 2 })}
                </td>
                {milestoneElements}
                <td className="tw-p-4 tw-text-right tw-border-r-2"></td>
                <td className="tw-p-4 tw-text-right tw-border-r-2">{formatFigure(totalValue, formatToFixedMoneyWithLocale, { precision: 2 })}</td>
                <td className="tw-p-4 tw-text-right tw-border-r-2"></td>
            </tr>
            {showCommittedChildren && <>{contractRows}</>}
        </>
    );
}
